"use client";
import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from 'next/navigation';
import useProfile from '../hooks/useProfile';
import Footer from "../components/Footer";
import {
  Badge,
  GradientText,
  DividerRow,
  ScrollReveal,
  StaggerGroup,
  StaggerItem,
} from "../../components/shared";
import type { DividerRowItem } from "../../components/shared";
import {
  GiftIcon,
  ShieldCheckIcon,
  GlobeIcon,
  LockIcon,
  ZapIcon,
  CopyIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
} from "../../components/shared/icons";

const wallets = [
  {
    label: "Ethereum / EVM",
    address: "0x32f70478D25Cc1846F4b2AE7C977B5fA1e5C7969",
    qr: "/logos/ethereum.png"
  },
  {
    label: "Bitcoin",
    address: "bc1q8664htax0udktaj634ud7nc7smdn5edt0y7c0z",
    qr: "/logos/bitcoin.png"
  },
  {
    label: "BNB",
    address: "0x32f70478D25Cc1846F4b2AE7C977B5fA1e5C7969",
    qr: "/logos/binance.png"
  },
  {
    label: "Solana",
    address: "G5oEZV7nU9oo1b3MmFj5s1HBLKJHSFjwUVtyzU3NT7bi",
    qr: "/logos/solana.png"
  }
];

const TRUST_HIGHLIGHTS: { label: string; icon: typeof ShieldCheckIcon; accent: string }[] = [
  { label: "On-Chain & Transparent", icon: ShieldCheckIcon, accent: "#22C55E" },
  { label: "Wallet-to-Wallet, No Fees", icon: ZapIcon, accent: "#00D4FF" },
  { label: "Multi-Chain Support", icon: GlobeIcon, accent: "#3B82F6" },
  { label: "Secure by Design", icon: LockIcon, accent: "#A855F7" },
];

const TRUST_ITEMS: DividerRowItem[] = TRUST_HIGHLIGHTS.map((h) => {
  const Icon = h.icon;
  return {
    key: h.label,
    content: (
      <>
        <Icon size={17} className="shrink-0" style={{ color: h.accent }} />
        <span
          className="min-w-0 text-sm font-semibold leading-tight"
          style={{ color: "#f1f5f9" }}
        >
          {h.label}
        </span>
      </>
    ),
  };
});

/**
 * Donate page, restyled to match the rest of the Reqrium dashboard (glass
 * hero panel, gradient headline, shared badge/icon system). All real
 * functionality is untouched: the same four real wallet addresses + QR
 * codes, the same query-param-driven donation-request flow (owner check via
 * useProfile + POST /api/donation-request), and the same decorative
 * message/amount form (which was never wired to a backend before this pass
 * either - restyled only, not given new fake behavior). Footer added at the
 * bottom to match the rest of the site's page-level Footer convention.
 */
export default function DonatePage() {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const router = useRouter();
  const { profile, loading: profileLoading } = useProfile();

  const [queryAddress, setQueryAddress] = useState<string | null>(null);
  const [queryChain, setQueryChain] = useState<string | null>(null);

  // read query params client-side to avoid useSearchParams prerender issues
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    setQueryAddress(sp.get('address'));
    setQueryChain(sp.get('chain'));
  }, []);

  // Create donation request form state
  const [platform, setPlatform] = useState('');
  const [url, setUrl] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    if (!platform || !url) {
      setCreateError('Platform and URL/address are required');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch('/api/donation-request', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, url, description: descriptionInput })
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateError(data?.message || 'Failed to create donation request');
      } else {
        setCreateSuccess(true);
        // redirect back to wallet page when created
        setTimeout(() => {
          router.push(queryChain && queryAddress ? `/wallet/${queryChain}/${queryAddress}` : '/');
        }, 1200);
      }
    } catch (err) {
      setCreateError((err as Error)?.message ?? 'Network error');
    } finally {
      setCreating(false);
    }
  }

  const profileAddress = profile && typeof (profile as { address?: unknown }).address === 'string' ? (profile as { address?: unknown }).address as string : null;
  const isOwner = !!(profileAddress && queryAddress && profileAddress.toLowerCase() === queryAddress.toLowerCase());

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        backgroundColor: "#0A0F1C",
        backgroundImage:
          "radial-gradient(circle at top, rgba(168,85,247,0.07), transparent 55%), radial-gradient(circle at bottom, rgba(0,212,255,0.07), transparent 55%)",
      }}
    >
      <main id="content" className="flex-1 w-full flex flex-col items-center">
        {/* Hero */}
        <section aria-labelledby="donate-heading" className="page-section relative w-full pt-24 pb-5 sm:pt-28 sm:pb-6">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <div
                className="section-panel relative overflow-hidden rounded-[32px] border px-5 py-8 sm:px-10 sm:py-10 text-center sm:text-left"
                style={{
                  borderColor: "rgba(91,127,255,0.28)",
                  background: "linear-gradient(180deg, rgba(16,24,39,0.75), rgba(10,15,28,0.9))",
                  boxShadow:
                    "0 0 0 1px rgba(168,85,247,0.08) inset, 0 30px 70px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(168,85,247,0.25)",
                }}
              >
                <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                  <div
                    className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(168,85,247,0.22), transparent 70%)" }}
                  />
                  <div
                    className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(0,212,255,0.2), transparent 70%)" }}
                  />
                </div>

                <div className="relative z-10 flex flex-col items-center sm:items-start">
                  <Badge
                    icon={<GiftIcon size={14} />}
                    borderColor="rgba(168,85,247,0.4)"
                    backgroundColor="rgba(168,85,247,0.1)"
                    textColor="#c084fc"
                  >
                    Direct &amp; On-Chain
                  </Badge>

                  <h1 id="donate-heading" className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                    <GradientText variant="brand">Support Reqrium</GradientText>
                  </h1>
                  <p className="mt-3 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "var(--muted-text, #94a3b8)" }}>
                    Send a donation directly to one of the wallets below. Every contribution helps keep
                    Reqrium&apos;s wallet intelligence, threat scanning, and charity verification running.
                  </p>

                  {queryAddress && queryChain ? (
                    <p
                      className="mt-4 inline-flex flex-wrap items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm"
                      style={{ borderColor: "rgba(91,127,255,0.25)", background: "rgba(8,12,22,0.5)", color: "#cbd5e1" }}
                    >
                      Creating a donation request for{" "}
                      <span className="font-mono text-cyan-300">{queryAddress}</span>
                      <span>on</span>
                      <span className="font-semibold text-slate-100">{queryChain}</span>
                    </p>
                  ) : null}

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <a href="#donation-wallets" className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_18px_36px_-18px_rgba(0,212,255,0.7)] transition hover:-translate-y-0.5">
                      Choose a wallet
                    </a>
                    <a href="#donation-request" className="inline-flex min-h-[48px] items-center justify-center rounded-full border border-white/10 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-slate-100 transition hover:bg-white/[0.06]">
                      Donation request details
                    </a>
                  </div>
                </div>

                <DividerRow
                  className="relative z-10 mt-10"
                  background="rgba(8,12,22,0.5)"
                  cellClassName="flex flex-1 items-center min-w-0 gap-2.5 px-4 py-3.5"
                  items={TRUST_ITEMS}
                />
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Wallets grid */}
        <section id="donation-wallets" aria-label="Donation wallets" className="page-section relative w-full py-8 sm:py-10">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <h2 className="text-lg sm:text-xl font-bold" style={{ color: "var(--text-base, #f1f5f9)" }}>
                Donate Directly
              </h2>
              <p className="mt-1 text-sm" style={{ color: "var(--muted-text, #94a3b8)" }}>
                Choose any wallet below and send support on-chain.
              </p>
            </ScrollReveal>

            <StaggerGroup className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {wallets.map((w) => (
                <StaggerItem key={w.label}>
                  <WalletCard label={w.label} address={w.address} img={w.qr} />
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </section>

        {/* Donation request creation (owner-only) */}
        {queryAddress && queryChain ? (
          <section id="donation-request" className="page-section relative w-full pb-4">
            <div className="mx-auto max-w-5xl">
              <div
                className="section-panel rounded-[28px] border p-5 sm:p-6"
                style={{
                  background: "var(--glass-bg, rgba(16,24,39,0.55))",
                  borderColor: "var(--color-card-border, rgba(91,127,255,0.18))",
                  backdropFilter: "blur(18px)",
                }}
              >
                {profileLoading ? (
                  <div className="text-sm" style={{ color: "var(--muted-text, #94a3b8)" }}>Checking account...</div>
                ) : isOwner ? (
                  <form onSubmit={handleCreate} className="flex flex-col gap-3 max-w-md mx-auto">
                    <h2 className="text-lg font-semibold text-center" style={{ color: "#f1f5f9" }}>
                      Create Donation Request
                    </h2>
                    <input
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
                      placeholder="Platform (e.g. OpenCollective)"
                      value={platform}
                      onChange={e => setPlatform(e.target.value)}
                    />
                    <input
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
                      placeholder="URL or wallet address"
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                    />
                    <textarea
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
                      placeholder="Description"
                      value={descriptionInput}
                      onChange={e => setDescriptionInput(e.target.value)}
                    />
                    <div className="flex gap-2 justify-center mt-1">
                      <button
                        type="submit"
                        disabled={creating}
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold shadow-[0_0_24px_-8px_rgba(0,212,255,0.5)] transition hover:-translate-y-0.5 disabled:opacity-60"
                      >
                        {creating ? 'Creating...' : 'Create Request'}
                      </button>
                      <button
                        type="button"
                        className="px-5 py-2.5 rounded-xl border border-white/10 text-sm font-semibold text-slate-200 hover:bg-white/5"
                        onClick={() => router.push(`/wallet/${queryChain}/${queryAddress}`)}
                      >
                        Cancel
                      </button>
                    </div>
                    {createError && (
                      <div className="flex items-center gap-2 justify-center text-sm text-red-400 mt-1">
                        <AlertTriangleIcon size={15} />
                        {createError}
                      </div>
                    )}
                    {createSuccess && (
                      <div className="flex items-center gap-2 justify-center text-sm text-emerald-400 mt-1">
                        <CheckCircleIcon size={15} />
                        Request created — redirecting...
                      </div>
                    )}
                  </form>
                ) : (
                  <div className="text-sm text-center" style={{ color: "#fde68a" }}>
                    To request donations for this wallet you must be the wallet owner and signed in.{" "}
                    <button className="underline font-semibold" onClick={() => router.push('/login')}>
                      Sign in
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {/* Optional message / amount */}
        <section className="relative w-full px-4 py-4">
          <div className="mx-auto max-w-5xl">
            <div
              className="rounded-2xl border p-5 sm:p-6"
              style={{
                background: "var(--glass-bg, rgba(16,24,39,0.55))",
                borderColor: "var(--color-card-border, rgba(91,127,255,0.18))",
                backdropFilter: "blur(18px)",
              }}
            >
              <form className="flex flex-col items-center gap-3 max-w-md mx-auto" onSubmit={handleSubmit}>
                <label className="text-sm font-semibold text-center" style={{ color: "#f1f5f9" }}>
                  Send a message or donation amount (optional)
                </label>
                <input
                  type="text"
                  placeholder="Amount (e.g. 0.05 ETH)"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/10 text-sm text-emerald-300 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
                <textarea
                  placeholder="Message (optional)"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold shadow-[0_0_24px_-8px_rgba(16,185,129,0.5)] transition hover:-translate-y-0.5"
                >
                  Send
                </button>
              </form>
              {submitted && (
                <p className="mt-4 text-center text-emerald-400 font-semibold">
                  Thank you for your support! 🎉
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Social + footnote */}
        <section className="relative w-full px-4 py-8 text-center">
          <div className="mx-auto max-w-5xl flex flex-col items-center gap-4">
            <div className="flex justify-center gap-4">
              <Link
                href="/discord"
                aria-label="Discord"
                className="grid h-12 w-12 place-items-center rounded-full border transition-all duration-300 hover:border-cyan-400/50"
                style={{ borderColor: "rgba(91,127,255,0.25)", background: "rgba(16,24,39,0.55)" }}
              >
                <Image src="/icons/discord.svg" alt="Discord" width={24} height={24} />
              </Link>
              <a
                href="mailto:support@reqrium.com"
                aria-label="Email"
                className="grid h-12 w-12 place-items-center rounded-full border transition-all duration-300 hover:border-cyan-400/50"
                style={{ borderColor: "rgba(91,127,255,0.25)", background: "rgba(16,24,39,0.55)" }}
              >
                <Image src="/icons/email.svg" alt="Email" width={24} height={24} />
              </a>
            </div>
            <p className="text-xs" style={{ color: "var(--muted-text, #94a3b8)" }}>
              Ethereum, Polygon, BNB, Bitcoin, Solana, and more supported.
            </p>
            <button
              className="mt-2 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border text-sm font-semibold text-slate-200 transition hover:-translate-y-0.5 hover:border-cyan-400/50"
              style={{ borderColor: "rgba(91,127,255,0.25)", background: "rgba(16,24,39,0.55)" }}
              onClick={() => router.push('/search')}
            >
              Back to Search
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function WalletCard({ label, address, img }: { label: string; address: string; img: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      className="group relative overflow-hidden rounded-2xl border p-5 flex items-center gap-4"
      style={{
        background: "var(--glass-bg, rgba(16,24,39,0.55))",
        borderColor: "var(--color-card-border, rgba(91,127,255,0.18))",
        backdropFilter: "blur(18px)",
      }}
    >
      <Image src={img} alt={label + " QR"} width={72} height={72} className="rounded-xl shadow-md shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm" style={{ color: "#f1f5f9" }}>{label}</p>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono text-xs break-all" style={{ color: "var(--muted-text, #94a3b8)" }}>{address}</span>
        </div>
        <button
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
          style={{
            color: copied ? "#22c55e" : "#00d4ff",
            background: copied ? "rgba(34,197,94,0.1)" : "rgba(0,212,255,0.1)",
          }}
          onClick={() => {
            navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
          title="Copy address"
        >
          {copied ? <CheckCircleIcon size={14} /> : <CopyIcon size={14} />}
          {copied ? 'Copied' : 'Copy address'}
        </button>
      </div>
    </div>
  );
}
